import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  TEMPLATE_LANGUAGE,
  TEMPLATE_STATUS,
  TEMPLATE_TYPE,
} from '../constants/messageTemplateKeys';
import { resolveSaveStatus } from '../lib/resolveSaveStatus';
import { selectTriggerValidationRowErrors } from '../store/templatesSlice';
import {
  buildTemplateValidationErrorsForAttempt,
  buildTemplateValidationState,
} from '../lib/buildTemplateValidationState';
import { buildTemplateButtonStates } from '../lib/buildTemplateButtonStates';
import {
  hasBasicInformationDraftErrors,
  hasBasicInformationErrors,
} from '../ui/create-template/basic-information/lib/buildBasicInformationErrors';
import { scrollToFirstFieldError } from '../lib/scrollToFirstFieldError';
import { computeDayOptionalByRow } from '../ui/create-template/basic-information/lib/sendConditionHelpers';
import { isLanguageEditable } from '../lib/languageHelpers';
import { useTemplateLeaveGuard } from './useTemplateLeaveGuard';
import { useTemplateSave } from './useTemplateSave';

const DEFAULT_BASIC_INFO_FORM = {
  templateName: '',
  type: TEMPLATE_TYPE.MARKETING,
  language: TEMPLATE_LANGUAGE.ENGLISH,
  subjects: [''],
  selectedCampaignIds: [],
  selectedTagIds: [],
  selectedTenantIds: [],
  isForAllTenants: false,
  triggerId: '',
  sendConditions: [{ sendCondition: '', day: '' }],
};

const SETTLE_DELAY_MS = 250;

// Key-order-independent serialization for the dirty check: the child form emits keys in a different
// order than DEFAULT_BASIC_INFO_FORM, and a plain JSON.stringify would then differ on order alone.
const stableStringify = (value) =>
  JSON.stringify(value, (_key, val) =>
    val && typeof val === 'object' && !Array.isArray(val)
      ? Object.keys(val)
          .sort()
          .reduce((acc, k) => {
            acc[k] = val[k];
            return acc;
          }, {})
      : val,
  );

export function useTemplateForm({
  channelKey,
  isEditMode,
  templateId,
  initialBasicInfoForm,
  isSourceDataLoading,
  editorContent = '',
  customStyle = '',
  logoUrl = '',
  checkedDisclosures = [],
  senderInfo = null,
  currentStatus = null,
  isAdmin = false,
  ownTenantId = null,
  isEditorReady = true,
}) {
  const [basicInfoSaveAttempt, setBasicInfoSaveAttempt] = useState(0);
  // Bumped on every save/publish/deactivate/leave attempt so the error snapshot re-runs each
  // time. basicInfoSaveAttempt only changes on publish and hasTriedSave is a one-way latch, so
  // neither can re-trigger the snapshot on a repeated draft save / deactivate.
  const [revealNonce, setRevealNonce] = useState(0);
  const [hasTriedSave, setHasTriedSave] = useState(false);
  const [hasTriedPublish, setHasTriedPublish] = useState(false);
  const [basicInfoForm, setBasicInfoForm] = useState(DEFAULT_BASIC_INFO_FORM);
  // Touched gating reported by the Basic Information form, so button states reflect only
  // the errors the user can see (an untouched/just-added field must not silently disable
  // a button). Submit-time validation ignores this and re-checks the full form.
  const [basicInfoTouched, setBasicInfoTouched] = useState(null);
  const [baseline, setBaseline] = useState(null);

  const hasTemplateId = String(templateId ?? '').trim().length > 0;

  // Merge, not replace: the mapped template carries no tenant keys — those belong to the child
  // form (its admin own-tenant default) and only reach us through onFormChange. Replacing drops
  // them, and the tenant gating then reads an empty selection while the chip still shows one.
  useEffect(() => {
    if (initialBasicInfoForm != null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBasicInfoForm((prev) => ({ ...prev, ...initialBasicInfoForm }));
    }
  }, [initialBasicInfoForm]);

  // Per-row "Days may be empty" flags — the same rule the form uses, recomputed here from
  // the live form + the loaded template so the submit-time checks and button states agree.
  const dayOptionalByRow = useMemo(
    () =>
      computeDayOptionalByRow({
        isAdmin,
        isEditMode,
        triggerId: basicInfoForm?.triggerId,
        sendConditions: basicInfoForm?.sendConditions,
        initialTriggerId: initialBasicInfoForm?.triggerId,
        initialSendConditions: initialBasicInfoForm?.sendConditions,
      }),
    [isAdmin, isEditMode, basicInfoForm, initialBasicInfoForm],
  );

  const {
    hasDraftBlockingErrors,
    hasPublishBlockingErrors,
    hasTemplateNameBlockingError,
    hasSendConditionsBlockingError,
  } = buildTemplateValidationState({
    channelKey,
    form: basicInfoForm,
    hasTriedSave,
    hasTriedPublish,
    touchState: basicInfoTouched,
    dayOptionalByRow,
  });

  // Backend trigger validation (e.g. "This Send Condition is already used for another
  // Template.") lives in Redux, separate from the client-side checks above. A live
  // conflict must block saving, deactivating, and publishing alike.
  const triggerValidationRowErrors = useSelector(selectTriggerValidationRowErrors);
  const hasTriggerValidationErrors = Object.values(triggerValidationRowErrors ?? {}).some(
    (rowError) => rowError?.day || rowError?.sendCondition || rowError?.nonField,
  );

  // Tenant gating is admin-only and create-only (for-tenants runs only on create).
  // Empty selection behaves like a required field; any non-own tenant restricts to a draft.
  const tenantIds = (basicInfoForm?.selectedTenantIds ?? []).map(String);
  const tenantSelectionEmpty = !isEditMode && isAdmin && tenantIds.length === 0;
  const hasNonOwnTenant =
    !isEditMode && isAdmin && tenantIds.some((id) => id !== String(ownTenantId ?? ''));

  // Errors that block leaving — i.e. the form cannot be saved with the status a save-on-leave
  // would use. Computed ungated (full re-check, no touched/save gating) so the leave guard can
  // decide whether to surface errors regardless of whether the user has hit Save yet.
  const draftBlockingAttemptErrors = buildTemplateValidationErrorsForAttempt({
    channelKey,
    form: basicInfoForm,
    isPublish: false,
    dayOptionalByRow,
  });
  const publishBlockingAttemptErrors = buildTemplateValidationErrorsForAttempt({
    channelKey,
    form: basicInfoForm,
    isPublish: true,
    dayOptionalByRow,
  });

  // Subject Line / Campaign block leaving only when a save-on-leave would publish — a draft or
  // inactive template saves with them empty, so the guard must not surface what Save allows.
  const savesAsPublished = resolveSaveStatus(currentStatus) === TEMPLATE_STATUS.PUBLISHED;
  const hasPublishLevelBlockingErrors =
    savesAsPublished && hasBasicInformationErrors(publishBlockingAttemptErrors);

  const hasBlockingErrors =
    hasBasicInformationDraftErrors(draftBlockingAttemptErrors) ||
    hasPublishLevelBlockingErrors ||
    hasTriggerValidationErrors ||
    tenantSelectionEmpty;

  const checkedDisclosureIds = checkedDisclosures
    .map((d) => d.id)
    .sort()
    .join(',');

  // On admin create the own-tenant default is applied by an effect once tenants load, not by the
  // user — so treat "empty" and "own-tenant-only" as the same, or that async default reads as an
  // edit and makes a pristine form look dirty (blocking Back and surfacing required-field errors).
  const ownTenantIdStr = String(ownTenantId ?? '');
  const normalizeForBaseline = (form) => {
    if (isEditMode || !isAdmin || !form) return form;
    const ids = (form.selectedTenantIds ?? []).map(String);
    const isOwnOnlyOrEmpty = ids.length === 0 || (ids.length === 1 && ids[0] === ownTenantIdStr);
    if (!isOwnOnlyOrEmpty) return form;
    return { ...form, selectedTenantIds: [], isForAllTenants: false };
  };

  // Fill the default shape first: the loaded template maps to a form without the tenant keys while
  // the child form always emits them, and a key merely appearing must not read as an edit.
  const basicInfoFormJson = stableStringify({
    ...DEFAULT_BASIC_INFO_FORM,
    ...normalizeForBaseline(basicInfoForm),
  });

  const settleTimerRef = useRef(null);

  // The editor counts as initial data too: on a cold load TinyMCE's normalization of the server
  // HTML lands after the settle window, contradicting a baseline that has already frozen.
  const isInitialDataReady =
    isEditorReady && (isEditMode ? !isSourceDataLoading && initialBasicInfoForm != null : true);

  useEffect(() => {
    if (!isInitialDataReady) {
      if (settleTimerRef.current != null) {
        clearTimeout(settleTimerRef.current);
        settleTimerRef.current = null;
      }
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBaseline(null);
      return;
    }
    if (baseline != null) return;
    if (settleTimerRef.current != null) clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(() => {
      setBaseline({ basicInfoFormJson, editorContent, checkedDisclosureIds });
      settleTimerRef.current = null;
    }, SETTLE_DELAY_MS);
    return () => {
      if (settleTimerRef.current != null) {
        clearTimeout(settleTimerRef.current);
        settleTimerRef.current = null;
      }
    };
  }, [isInitialDataReady, baseline, basicInfoFormJson, editorContent, checkedDisclosureIds]);

  const hasUnsavedChanges =
    baseline != null &&
    (baseline.basicInfoFormJson !== basicInfoFormJson ||
      baseline.editorContent !== editorContent ||
      baseline.checkedDisclosureIds !== checkedDisclosureIds);

  const onSaveAttempt = useCallback(({ isPublish }) => {
    setRevealNonce((n) => n + 1);
    if (isPublish) {
      setHasTriedPublish(true);
      setBasicInfoSaveAttempt((n) => n + 1);
    } else {
      setHasTriedSave(true);
    }
  }, []);

  // Reveal every (currently hidden) validation error and scroll to the first one. Bumping the
  // save attempt marks visible fields as touched (see useBasicInformationForm), which is what
  // surfaces the gated required errors.
  const surfaceValidationErrors = useCallback(() => {
    setHasTriedSave(true);
    if (hasPublishLevelBlockingErrors) {
      setHasTriedPublish(true);
      setBasicInfoSaveAttempt((n) => n + 1);
    }
    setRevealNonce((n) => n + 1);
    scrollToFirstFieldError();
  }, [hasPublishLevelBlockingErrors]);

  const {
    discardDialogOpen,
    showSaveOnDiscard,
    skipNextBlock,
    onLeaveTemplatesClick,
    onDiscardClick,
    proceedNavigation,
    onDiscardConfirm,
    onDiscardClose,
  } = useTemplateLeaveGuard({
    hasUnsavedChanges,
    hasBlockingErrors,
    onBlockedWithErrors: surfaceValidationErrors,
  });

  const tryPublish = useCallback(() => {
    onSaveAttempt({ isPublish: true });
    const errors = buildTemplateValidationErrorsForAttempt({
      channelKey,
      form: basicInfoForm,
      isPublish: true,
      dayOptionalByRow,
    });
    if (hasBasicInformationErrors(errors) || tenantSelectionEmpty) {
      scrollToFirstFieldError();
      return false;
    }
    return true;
  }, [basicInfoForm, channelKey, onSaveAttempt, tenantSelectionEmpty, dayOptionalByRow]);

  const { saveTemplate, submitTemplate, deactivateTemplate, isSubmitting } = useTemplateSave({
    channelKey,
    isEditMode,
    templateId,
    hasTemplateId,
    isSourceDataLoading,
    basicInfoForm,
    editorContent,
    customStyle,
    logoUrl,
    checkedDisclosures,
    senderInfo,
    onSaveAttempt,
    skipNextBlock,
    tenantSelectionEmpty,
    useForTenantsOnSave: hasNonOwnTenant,
    dayOptionalByRow,
    canEditLanguage: isLanguageEditable({ isEditMode, currentStatus }),
  });

  // An active template counts as published here: saving edits republishes it (the API
  // rewrites an `active` update back to `published`), so it gets the same actions —
  // no separate Save button, and Publish only enabled once something changed.
  const isAlreadyPublished =
    currentStatus === TEMPLATE_STATUS.PUBLISHED || currentStatus === TEMPLATE_STATUS.ACTIVE;

  const { isSaveDisabled, isDeactivateDisabled, isPublishDisabled } = buildTemplateButtonStates({
    hasDraftBlockingErrors: hasDraftBlockingErrors || hasTriggerValidationErrors,
    hasPublishBlockingErrors: hasPublishBlockingErrors || hasTriggerValidationErrors,
    hasTemplateNameBlockingError,
    hasSendConditionsBlockingError,
    isSubmitting,
    isSourceDataLoading,
    hasTemplateId,
    isAlreadyPublished,
    hasUnsavedChanges,
    tenantRequiredActive: tenantSelectionEmpty && (hasTriedSave || hasTriedPublish),
    restrictToDraft: hasNonOwnTenant,
  });

  // Saving on navigate-away preserves the current status (see resolveSaveStatus)
  // instead of demoting the template to draft.
  const onSaveAndProceed = useCallback(async () => {
    const saved = await saveTemplate(resolveSaveStatus(currentStatus));
    if (saved) {
      proceedNavigation();
    }
  }, [saveTemplate, proceedNavigation, currentStatus]);

  return {
    basicInfoForm,
    setBasicInfoForm,
    setBasicInfoTouched,
    basicInfoSaveAttempt,
    revealNonce,
    hasTriedSave,
    discardDialogOpen,
    showSaveOnDiscard,
    isSaveDisabled,
    isDeactivateDisabled,
    isPublishDisabled,
    isAlreadyPublished,
    onLeaveTemplatesClick,
    onDiscardClick,
    onDiscardConfirm,
    onDiscardClose,
    onSaveAndProceed,
    submitTemplate,
    deactivateTemplate,
    tryPublish,
  };
}
